/**
 * Pure classification of a promo code once Shopify has tried to apply it and
 * the discount service (anatae-discount-app-be) has been queried.
 *
 * Kept free of DOM and network so it can be unit tested with `npm test`.
 */

/**
 * Codes generated by third-party apps are unknown to the discount service.
 * When Shopify does not apply them in the cart we cannot tell a typo from a
 * code that will only validate at checkout (customer-bound, login required…),
 * so these shapes are treated as "pending until checkout".
 */
export const APP_CODE_PATTERNS = [
  /^WELCOME-/i, // Klaviyo
  /^[A-Z0-9]{15}$/, // Loyoly
];

export const isAppCode = (code) => APP_CODE_PATTERNS.some((pattern) => pattern.test(code));

const isB2BCode = (code) => code.startsWith('B2B');

/**
 * Pro codes (prefixed B2B) are reserved to B2B customers and B2B customers may
 * only use pro codes. Returns the error status, or null when the code may be used.
 * Checked before the code is sent to Shopify so a refused code never reaches checkout.
 */
export const getB2BError = (code, isB2BCustomer) => {
  if (isB2BCustomer && !isB2BCode(code)) return 'b2b_code_error';
  if (!isB2BCustomer && isB2BCode(code)) return 'b2c_code_error';
  return null;
};

/**
 * @param {object} params
 * @param {string} params.code – Uppercased promo code
 * @param {boolean} params.applicable – `applicable` flag from cart.js discount_codes
 * @param {object|undefined} params.service – Raw discount service response (undefined on network failure)
 * @param {boolean} params.isB2BCustomer
 * @param {boolean} [params.otherCodeApplied] – Another code is already applied on the cart
 * @returns {{status: 'b2b_code_error'|'b2c_code_error'|'invalid'|'app_pending'|'minimum_not_reached'|'gift'|'success', freeShipping?: boolean}}
 */
export const classifyPromocode = ({ code, applicable, service, isB2BCustomer, otherCodeApplied = false }) => {
  const b2bError = getB2BError(code, isB2BCustomer);
  if (b2bError) return { status: b2bError };

  const discount = service?.success === true ? service.discount : null;

  if (!discount) {
    if (applicable) return { status: 'success', freeShipping: false };
    return { status: isAppCode(code) ? 'app_pending' : 'invalid' };
  }

  if (discount.status && discount.status !== 'ACTIVE') return { status: 'invalid' };

  const classes = discount.discountClasses || [];
  const hasGift = typeof service.giftProductId === 'string' && service.giftProductId !== '';

  if (hasGift && (applicable || discount.__typename === 'DiscountCodeBxgy')) {
    return { status: 'gift' };
  }

  if (!applicable) {
    // Buy X get Y: the code becomes valid once the products are in the cart.
    // App discounts (Jolly): their conditions are evaluated by the app at
    // checkout. In both cases the code is kept for the next step.
    if (discount.__typename === 'DiscountCodeBxgy' || discount.__typename === 'DiscountCodeApp') {
      return { status: 'app_pending' };
    }
    // An order discount refused on a non-empty cart is a minimum not reached,
    // unless it simply cannot combine with a code already applied.
    if (classes.includes('ORDER') && !otherCodeApplied) return { status: 'minimum_not_reached' };
    return { status: 'invalid' };
  }

  return { status: 'success', freeShipping: classes.includes('SHIPPING') };
};
